import { StyleSheet, Text, TouchableOpacity, View } from 'react-native';

import { Colors } from '@/constants/theme';
import { Spacing, BorderRadius } from '@/constants/spacing';
import { useColorScheme } from '@/hooks/use-color-scheme';
import { getBodyFont } from '@/constants/typography';
import type { ArchivedSuggestion } from '@/types/ai-suggestions';

type SuggestionHistoryItemProps = {
  archivedSuggestion: ArchivedSuggestion;
  onPress?: () => void;
};

/**
 * SuggestionHistoryItem - Individual row in the suggestion history list.
 * Shows title, truncated description, action badge, and relative timestamp.
 */
export function SuggestionHistoryItem({ archivedSuggestion, onPress }: SuggestionHistoryItemProps) {
  const colorScheme = useColorScheme();
  const palette = Colors[colorScheme ?? 'light'];
  const { suggestion, action, archivedAt } = archivedSuggestion;

  const isAdded = action === 'added_to_calendar' || action === 'added_to_todo';
  const badgeLabel = action === 'added_to_calendar' ? 'Calendar' : action === 'added_to_todo' ? 'To-do' : 'Dismissed';
  const badgeColor = isAdded ? palette.primary : palette.textMuted;

  return (
    <TouchableOpacity
      style={[styles.container, { borderBottomColor: palette.borderMuted }]}
      onPress={onPress}
      activeOpacity={0.7}
    >
      <View style={styles.content}>
        <Text
          style={[styles.title, { color: palette.text, fontFamily: getBodyFont() }]}
          numberOfLines={1}
        >
          {suggestion.title}
        </Text>
        <Text
          style={[styles.description, { color: palette.textMuted, fontFamily: getBodyFont() }]}
          numberOfLines={1}
        >
          {suggestion.description}
        </Text>
      </View>

      <View style={styles.meta}>
        <View style={[styles.badge, { backgroundColor: badgeColor + '20' }]}>
          <Text style={[styles.badgeText, { color: badgeColor, fontFamily: getBodyFont() }]}>
            {badgeLabel}
          </Text>
        </View>
        <Text style={[styles.timestamp, { color: palette.textMuted, fontFamily: getBodyFont() }]}>
          {formatRelativeTime(archivedAt)}
        </Text>
      </View>
    </TouchableOpacity>
  );
}

function formatRelativeTime(isoString: string): string {
  const now = Date.now();
  const then = new Date(isoString).getTime();
  const diffMs = now - then;
  const diffMinutes = Math.floor(diffMs / 60_000);
  const diffHours = Math.floor(diffMs / 3_600_000);
  const diffDays = Math.floor(diffMs / 86_400_000);

  if (diffMinutes < 1) return 'Just now';
  if (diffMinutes < 60) return `${diffMinutes}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays}d ago`;
  return new Date(isoString).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

const styles = StyleSheet.create({
  container: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: Spacing.md,
    paddingHorizontal: Spacing.xl,
    borderBottomWidth: 1,
  },
  content: {
    flex: 1,
    marginRight: Spacing.md,
    gap: Spacing.xxs,
  },
  title: {
    fontSize: 15,
    fontWeight: '600',
  },
  description: {
    fontSize: 13,
  },
  meta: {
    alignItems: 'flex-end',
    gap: Spacing.xs,
  },
  badge: {
    paddingHorizontal: Spacing.sm,
    paddingVertical: Spacing.xxs,
    borderRadius: BorderRadius.full,
  },
  badgeText: {
    fontSize: 11,
    fontWeight: '600',
  },
  timestamp: {
    fontSize: 11,
  },
});
